import React, { FC, ReactNode } from "react";
import OnboardingTopBar from "./OnboardingTopBar";
import OnboardingStepRail from "./OnboardingStepRail";
import StaySummary from "./StaySummary";
import { OnboardingStep, StepId } from "./onboardingSteps";
import { OnboardingState } from "./panels/types";
import { useLocale } from "../i18n/locale";

export interface OnboardingWorkspaceProps {
  steps: OnboardingStep[];
  activeId: StepId;
  state: OnboardingState;
  onSelect: (id: StepId) => void;
  /** Closes the rail to navigation once the journey is finished. */
  locked?: boolean;
  /** The active step's panel — heading, body and footer. */
  children: ReactNode;
  className?: string;
}

/**
 * The three-column shell: step rail, swapping centre column, fixed stay
 * summary.
 *
 * The columns are proportional rather than fixed so the centre keeps its
 * measure on a wide screen without the rails growing with it. Below 1100px the
 * stay summary drops — it is context, not task — and below 760px the whole
 * thing stacks and the rail collapses to its eyebrow.
 */
const OnboardingWorkspace: FC<OnboardingWorkspaceProps> = ({
  steps,
  activeId,
  state,
  onSelect,
  locked = false,
  children,
  className = "",
}) => {
  const { locale } = useLocale();

  return (
    // lang scopes to the onboarding subtree; see OnboardingShell for why it is
    // not set on <html>.
    <div
      lang={locale}
      className={`nc-OnboardingWorkspace min-h-screen bg-cream-page font-body text-ink ${className}`}
      data-nc-id="OnboardingWorkspace"
    >
      <OnboardingTopBar />

      <div className="mx-auto grid max-w-[1700px] grid-cols-1 bg-white min-[760px]:min-h-[calc(100vh-84px)] min-[760px]:grid-cols-[290px_minmax(0,1fr)] min-[1100px]:grid-cols-[minmax(260px,27%)_minmax(0,1fr)_minmax(330px,29%)]">
        <OnboardingStepRail
          steps={steps}
          activeId={activeId}
          state={state}
          onSelect={onSelect}
          locked={locked}
        />

        <main className="flex min-w-0 justify-center bg-white px-[22px] pb-10 pt-8 min-[760px]:px-[50px] min-[760px]:py-[54px] min-[1100px]:px-[clamp(40px,5vw,82px)]">
          <div className="w-full max-w-[660px]">{children}</div>
        </main>

        {/* Context rather than task, so it yields the moment the columns get
            tight. It returns at the bottom of the stack on a phone — dropping
            the property, address and dates entirely left the tenant no way to
            check they are onboarding the right stay — but stays out between
            760 and 1100, where a third grid child wraps into the rail's 290px
            column and reads as broken. */}
        <StaySummary className="min-[760px]:hidden min-[1100px]:block" />
      </div>
    </div>
  );
};

export default OnboardingWorkspace;
